import { useState, useEffect, useCallback, useRef } from "react";
import { FiArrowLeft, FiCheck, FiLogIn, FiLogOut } from "react-icons/fi";
import { apiGet, apiPost } from "./api";

/* ─── design tokens via brand.css variables ─── */
const fmtH = (h) => h > 0 ? (h.toFixed(1).replace(".0", "") + "h") : "—";

const fmtTime12 = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
};

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function buildWeekDays(weekStart, records) {
  const start = new Date(weekStart);
  const today = new Date().toDateString();
  return DAY_NAMES.map((dayName, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const dateStr = d.toDateString();
    const dayRecords = records.filter(r => new Date(r.clock_in).toDateString() === dateStr);
    const hours = dayRecords.reduce((s, r) => s + (r.worked_hours || 0), 0);
    const firstIn  = dayRecords[0]?.clock_in  ? fmtTime12(dayRecords[0].clock_in)  : null;
    const lastOut  = dayRecords[dayRecords.length - 1]?.clock_out
      ? fmtTime12(dayRecords[dayRecords.length - 1].clock_out) : null;
    const isToday  = dateStr === today;
    const isActive = dayRecords.some(r => !r.clock_out); // open shift
    return {
      day: dayName,
      date: d.toLocaleDateString("en-US", { day: "numeric", month: "short" }),
      in: firstIn,
      out: lastOut,
      hours,
      today: isToday,
      active: isActive,
    };
  });
}

function initials(name) {
  if (!name) return "?";
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();
}

const ROLE_LABEL = { OWNER: "Owner", MANAGER: "Manager", EMPLOYEE: "Cashier" };

/* ─── Clock action confirmation ─── */
function ClockConfirm({ action, name }) {
  const isIn = action === "in";
  return (
    <div style={{ position: "fixed", inset: 0, background: isIn ? "var(--success)" : "var(--n-700)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-head)", color: "#fff", userSelect: "none" }}>
      <div style={{ width: 80, height: 80, borderRadius: 99, background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 24 }}>
        <FiCheck size={40} color="#fff" strokeWidth={2.5} />
      </div>
      <div style={{ fontSize: 36, fontWeight: 800, letterSpacing: "-.02em" }}>{isIn ? "Clocked In!" : "Clocked Out!"}</div>
      <div style={{ fontSize: 18, opacity: 0.75, marginTop: 8 }}>{name}</div>
      <div style={{ fontSize: 14, opacity: 0.45, marginTop: 28 }}>Returning to home screen…</div>
    </div>
  );
}

/* ─── Main ─── */
export default function Dashboard({ token, role, onDone }) {
  const [weeklyData, setWeeklyData]     = useState(null);
  const [loading, setLoading]           = useState(true);
  const [currentSession, setCurrentSession] = useState(null);
  const [clockAction, setClockAction]   = useState(null);
  const [toggling, setToggling]         = useState(false);
  const [now, setNow]                   = useState(new Date());
  const returnTimer = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => () => clearTimeout(returnTimer.current), []);

  const fetchData = useCallback(async () => {
    try {
      const res = await apiGet("/api/weekly-attendance", token);
      const data = await res.json();
      setWeeklyData(data);
      setCurrentSession(data.records?.find(r => !r.clock_out) ?? null);
    } catch { /* leave stale */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleClock = async () => {
    if (toggling) return;
    setToggling(true);
    const wasIn = !!currentSession;
    try {
      const res = await apiPost("/api/toggle-clock", {}, token);
      if (res.ok) {
        setClockAction(wasIn ? "out" : "in");
        fetchData();
        returnTimer.current = setTimeout(() => onDone(), 3000);
      }
    } catch { /* ignore */ }
    finally { setToggling(false); }
  };

  if (clockAction) return <ClockConfirm action={clockAction} name={weeklyData?.employee} />;

  if (loading) return (
    <div style={{ position: "fixed", inset: 0, background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{ color: "var(--sub)", fontFamily: "var(--font-body)", fontSize: 14 }}>Loading…</span>
    </div>
  );

  const clockedIn  = !!currentSession;
  const employee   = weeklyData?.employee || "";
  const roleLabel  = ROLE_LABEL[role] || role;
  const init       = initials(employee);
  const totalHours = weeklyData?.total_worked_hours || 0;
  const records    = weeklyData?.records || [];
  const weekStart  = weeklyData?.week_start;
  const weekEnd    = weeklyData?.week_end;
  const days       = weekStart ? buildWeekDays(weekStart, records) : [];
  const daysWorked = days.filter(d => d.hours > 0).length;
  const clockInTime = currentSession ? fmtTime12(currentSession.clock_in) : null;

  const weekLabel = weekStart && weekEnd
    ? `${new Date(weekStart).toLocaleDateString("en-US", { day: "numeric", month: "short" })} – ${new Date(weekEnd).toLocaleDateString("en-US", { day: "numeric", month: "short" })}`
    : "";

  return (
    <div style={{ position: "fixed", inset: 0, background: "var(--bg)", display: "flex", flexDirection: "column", fontFamily: "var(--font-body)", overflowY: "auto" }}>

      {/* scrollable content */}
      <div style={{ flex: 1, padding: "4px 56px 44px", minHeight: 0 }}>

        {/* page header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 22, paddingTop: 20 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button onClick={onDone} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 8, cursor: "pointer", display: "flex" }}>
                <FiArrowLeft size={18} color="var(--sub)" />
              </button>
              <h1 style={{ fontFamily: "var(--font-head)", fontSize: 29, fontWeight: 800, letterSpacing: "-.025em", margin: 0, color: "var(--ink)" }}>Attendance</h1>
              <span style={{ fontFamily: "var(--font-mm)", fontSize: 17, color: "var(--n-400)" }}>အလုပ်တက်ဆင်း</span>
            </div>
            <div style={{ fontSize: 14, color: "var(--sub)", marginTop: 8, marginLeft: 50 }}>
              {now.toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
          </div>
          <button onClick={onDone}
            style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--primary)", color: "#fff", border: "none", borderRadius: "var(--r-sm)", padding: "11px 18px", cursor: "pointer", fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14.5, height: 48 }}>
            Continue to Inventory →
          </button>
        </div>

        {/* personal status banner */}
        <div style={{ background: "var(--surface)", border: "1px solid var(--blue-100)", borderRadius: "var(--r-lg)", boxShadow: "var(--shadow-sm)", padding: "22px 26px", marginBottom: 16, display: "flex", alignItems: "center", gap: 24 }}>
          {/* avatar */}
          <div style={{ width: 60, height: 60, borderRadius: 17, background: "var(--blue-50)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-head)", fontSize: 21, fontWeight: 800, color: "var(--primary)", flexShrink: 0 }}>
            {init}
          </div>
          {/* name + status */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, color: "var(--ink)" }}>{employee}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "var(--blue-50)", color: "var(--primary)", borderRadius: 999, padding: "4px 10px", fontSize: 12.5, fontWeight: 700 }}>
                <span style={{ width: 6, height: 6, borderRadius: 3, background: "var(--primary)" }} />{roleLabel}
              </span>
            </div>
            <div style={{ fontSize: 13.5, color: "var(--sub)", marginTop: 5 }}>
              {clockedIn ? `Clocked in at ${clockInTime} · on shift` : "Not clocked in today"}
            </div>
          </div>
          {/* week mini-stats */}
          <div style={{ display: "flex", gap: 30, paddingRight: 8 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: "var(--sub)", fontWeight: 600 }}>This week</div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 24, fontWeight: 800, color: "var(--ink)", fontVariantNumeric: "tabular-nums", marginTop: 3 }}>{fmtH(totalHours)}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: "var(--sub)", fontWeight: 600 }}>Days</div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 24, fontWeight: 800, color: "var(--ink)", fontVariantNumeric: "tabular-nums", marginTop: 3 }}>{daysWorked}</div>
            </div>
          </div>
          {/* clock button */}
          <button onClick={toggleClock} disabled={toggling}
            style={{ display: "flex", alignItems: "center", gap: 8, height: 58, padding: "0 26px", fontSize: 16, fontFamily: "var(--font-head)", fontWeight: 700, borderRadius: "var(--r-sm)", border: "none", cursor: toggling ? "default" : "pointer", background: clockedIn ? "var(--surface)" : "var(--accent)", color: clockedIn ? "var(--ink)" : "#fff", boxShadow: clockedIn ? "inset 0 0 0 1px var(--line)" : "none", opacity: toggling ? 0.5 : 1, flexShrink: 0 }}>
            {clockedIn ? <FiLogOut size={18} /> : <FiLogIn size={18} />}
            {clockedIn ? "Clock Out" : "Clock In"}
          </button>
        </div>

        {/* weekly timesheet */}
        <div style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-lg)", boxShadow: "var(--shadow-sm)", overflow: "hidden" }}>
          <div style={{ padding: "16px 22px", borderBottom: "1px solid var(--hair)", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>This Week</span>
              {weekLabel && <span style={{ fontSize: 13, color: "var(--sub)", marginLeft: 10 }}>{weekLabel}</span>}
            </div>
            <div style={{ fontSize: 13, color: "var(--sub)" }}>
              Total <b style={{ color: "var(--ink)", fontFamily: "var(--font-head)", fontVariantNumeric: "tabular-nums" }}>{fmtH(totalHours)}</b>
            </div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Day", "Clock In", "Clock Out", "Hours"].map((h, i) => (
                  <th key={h} style={{ textAlign: i === 0 ? "left" : "right", padding: "13px 22px", fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--sub)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map((d, i) => (
                <tr key={i} style={{ background: d.today ? "var(--blue-50)" : "transparent" }}>
                  <td style={{ padding: "13px 22px", borderTop: "1px solid var(--hair)" }}>
                    <span style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>{d.day}</span>
                    <span style={{ fontSize: 12.5, color: "var(--sub)", marginLeft: 8, fontVariantNumeric: "tabular-nums" }}>{d.date}</span>
                    {d.today && <span style={{ fontSize: 11, fontWeight: 700, color: "var(--primary)", marginLeft: 8 }}>TODAY</span>}
                  </td>
                  <td style={{ padding: "13px 22px", borderTop: "1px solid var(--hair)", textAlign: "right", fontFamily: "var(--font-body)", fontSize: 14, color: "var(--sub)", fontVariantNumeric: "tabular-nums" }}>
                    {d.in || "—"}
                  </td>
                  <td style={{ padding: "13px 22px", borderTop: "1px solid var(--hair)", textAlign: "right", fontFamily: "var(--font-body)", fontSize: 14, color: "var(--sub)", fontVariantNumeric: "tabular-nums" }}>
                    {d.active ? <span style={{ color: "var(--success)", fontWeight: 600 }}>On shift</span> : (d.out || "—")}
                  </td>
                  <td style={{ padding: "13px 22px", borderTop: "1px solid var(--hair)", textAlign: "right", fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>
                    {d.hours > 0 ? fmtH(d.hours) : <span style={{ color: "var(--n-300)", fontWeight: 400 }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
